import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API from '../utils/api';
import toast from 'react-hot-toast';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.15 } } };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(null);
  const [buyForm, setBuyForm] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    API.get('/plans').then(res => setPlans(res.data.plans)).finally(() => setLoading(false));
  }, []);

  const handleBuy = (plan) => {
    setBuyForm(plan);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email required');
    setPayLoading(buyForm.id);

    const loaded = await loadRazorpay();
    if (!loaded) { toast.error('Payment gateway failed to load'); setPayLoading(null); return; }

    try {
      const res = await API.post('/payment/create-order', {
        plan_id: buyForm.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      const { order, key, plan } = res.data;

      const rzpOptions = {
        key,
        amount: order.amount,
        currency: order.currency,
        name: 'InfraVelox',
        description: `${plan.name} Plan`,
        order_id: order.id,
        handler: async (response) => {
          try {
            await API.post('/payment/verify', {
              ...response,
              order_db_id: order.order_db_id,
              name: form.name,
              email: form.email,
              phone: form.phone,
              plan_id: buyForm.id,
            });
            toast.success('🎉 Payment successful! Check your email for login credentials.');
            setBuyForm(null);
          } catch {
            toast.error('Payment verification failed. Contact support.');
          }
        },
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#6366f1' },
        modal: { ondismiss: () => setPayLoading(null) },
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment initiation failed');
    } finally {
      setPayLoading(null);
    }
  };

  return (
    <>
      <Navbar />

      <section style={{ paddingTop: '120px', paddingBottom: '40px', textAlign: 'center' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="badge">💳 Plans & Pricing</span>
            <h1 className="section-title">
              Invest in Your <span className="text-gradient">DevOps Career</span>
            </h1>
            <p className="section-subtitle">
              All plans include expert support, file uploads, and email notifications. No hidden fees. Cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-sm">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.05))',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: 16,
              padding: '24px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 48,
              flexWrap: 'wrap',
              gap: 20,
              boxShadow: '0 10px 30px -10px rgba(16, 185, 129, 0.2)'
            }}
          >
            <div>
               <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                 <span style={{ background: '#10b981', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 800, letterSpacing: '0.05em' }}>100% FREE</span>
                 <h3 style={{ margin: 0, fontSize: 22, color: '#fff', fontWeight: 800 }}>Expert Architecture Audit</h3>
               </div>
               <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 15, maxWidth: 650, lineHeight: 1.6 }}>
                 Not sure which plan you need? Get a comprehensive, no-obligation review of your current cloud infrastructure. We'll identify security gaps, cost-saving opportunities, and bottlenecks <strong>completely free of charge</strong>.
               </p>
            </div>
            <Link to="/contact?subject=Free%20Architecture%20Audit" className="btn" style={{ background: '#10b981', color: '#fff', border: 'none', padding: '12px 24px', fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)' }}>
              Claim Free Audit →
            </Link>
          </motion.div>

          {loading ? (
            <div className="loading-container"><div className="loading-spinner" /></div>
          ) : (
            <motion.div
              className="pricing-grid"
              variants={stagger}
              initial="hidden"
              animate="show"
            >
              {[
                {
                  id: 'free-audit',
                  name: 'Free Audit',
                  description: 'Comprehensive review of your current cloud infrastructure.',
                  price: 0,
                  duration_days: 'Lifetime',
                  request_limit: 1,
                  features: [
                    'Full Architecture Review',
                    'Security Vulnerability Check',
                    'Cloud Cost Optimization Analysis',
                    'Performance Bottlenecks Report',
                    'Zero Commitment Required'
                  ]
                },
                ...plans
              ].map((plan, i) => (
                <motion.div
                  key={plan.id}
                  className={`pricing-card ${plan.name === 'Professional' ? 'featured' : ''}`}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                >
                  {plan.name === 'Professional' && <div className="featured-badge">⭐ Most Popular</div>}
                  {plan.id === 'free-audit' && <div className="featured-badge" style={{background: '#10b981'}}>🎁 100% Free</div>}
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-desc">{plan.description}</div>
                  <div className="plan-price">
                    <div>
                      <span className="price-currency">$</span>
                      <span className="price-amount text-gradient">{Number(plan.price).toLocaleString()}</span>
                    </div>
                    {plan.id === 'free-audit' ? (
                      <div className="price-period" style={{ marginBottom: 4 }}>One-time report</div>
                    ) : (
                      <div className="price-period" style={{ marginBottom: 4 }}>/month · {plan.duration_days} days</div>
                    )}
                    <div className="price-period" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
                      {plan.id === 'free-audit' 
                        ? '1 Complete Audit Report'
                        : plan.request_limit >= 100 
                        ? 'Unlimited support tasks' 
                        : `Up to ${plan.request_limit} support tasks (~${plan.request_limit * 2} eng. hours)`}
                    </div>
                  </div>
                  <ul className="plan-features">
                    {plan.features.map((f, fi) => (
                      <li key={fi}><span className="feature-check">✓</span>{f}</li>
                    ))}
                  </ul>
                  <motion.button
                    className={`btn ${plan.name === 'Professional' || plan.id === 'free-audit' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ 
                      width: '100%', 
                      justifyContent: 'center',
                      ...(plan.id === 'free-audit' ? { background: '#10b981', borderColor: '#10b981', color: '#fff' } : {}) 
                    }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                       if (plan.id === 'free-audit') {
                          window.location.href = '/contact?subject=Free%20Architecture%20Audit';
                       } else {
                          handleBuy(plan);
                       }
                    }}
                    disabled={payLoading === plan.id}
                  >
                    {payLoading === plan.id ? 'Processing...' : plan.id === 'free-audit' ? 'Claim Free Audit →' : `Get ${plan.name} →`}
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 32,
            marginTop: 64,
            textAlign: 'left'
          }}>
            <motion.div className="glass-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#fbbf24' }}>💡 Extra Add-Ons</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>Available for International & Enterprise clients. Upsell available at checkout or contact support.</p>
              <ul className="plan-features">
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> 24/7 On-call Support</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> Emergency Incident Support</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> Migration Services (One-time)</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> Security Audit (DevSecOps)</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> Cloud Cost Optimization</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> CI/CD Full Setup Package</li>
                <li><span className="feature-check" style={{color: '#fbbf24', background: 'rgba(251,191,36,0.15)'}}>+</span> K8s Cluster Setup</li>
              </ul>
            </motion.div>

            <motion.div className="glass-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📋 What counts as a "Task"?</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>We measure support by engineering hours required. Each task roughly correlates to ~1-2 hours of focused DevOps engineering.</p>
              <ul className="plan-features">
                <li><span className="feature-check">1</span> <b>Bug Fix / Troubleshooting</b></li>
                <li><span className="feature-check">1</span> <b>Basic Dockerfile / Review</b></li>
                <li><span className="feature-check">2</span> <b>Pipeline Setup (CI/CD)</b></li>
                <li><span className="feature-check">4</span> <b>Infrastructure as Code (Terraform)</b></li>
              </ul>
              <div style={{ marginTop: 24, padding: 12, borderRadius: 8, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--brand-primary)' }}>Supported Tools:</strong> AWS, Azure, GCP, Docker, Kubernetes, Terraform, GitHub Actions, GitLab CI, Jenkins, Nginx, Helm, Prometheus, Grafana.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Feature comparison */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{ textAlign: 'center', marginTop: 64 }}
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 24,
              marginTop: 40,
            }}>
              {[
                { icon: '⚡', title: 'Fast Responses', desc: 'SLA guarantees for all plans (Starter: 48h, Pro: 24h, Enterprise: 4h).' },
                { icon: '🔒', title: 'Secure Platform', desc: 'End-to-end encryption. Your code, configs and data stay entirely private.' },
                { icon: '📁', title: 'File Uploads', desc: 'Securely share logs, manifests, and screenshots with your tickets.' },
                { icon: '📧', title: 'Notification Alerts', desc: 'Get SMS & email alerts immediately when our experts respond.' },
              ].map((f, i) => (
                <motion.div key={i} className="glass-card" whileHover={{ y: -4 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{f.icon}</div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Payment Modal */}
      {buyForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setBuyForm(null)}>
          <motion.div
            className="modal-box"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
          >
            <div className="modal-header">
              <h2>🚀 Get {buyForm.name} Plan</h2>
              <button className="modal-close" onClick={() => setBuyForm(null)}>✕</button>
            </div>

            <div style={{
              background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontWeight: 600 }}>{buyForm.name} Plan</span>
              <span className="text-gradient" style={{ fontWeight: 800, fontSize: 22 }}>${Number(buyForm.price).toLocaleString()}</span>
            </div>

            <form onSubmit={submitPayment}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" placeholder="Your full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input className="form-control" type="email" placeholder="your@email.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-control" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>

              <div className="alert alert-info" style={{ marginTop: 8 }}>
                💡 Your login credentials will be sent to your email after payment.
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                disabled={payLoading === buyForm.id}
              >
                {payLoading === buyForm.id ? '⏳ Processing...' : `💳 Pay $${Number(buyForm.price).toLocaleString()} with Razorpay`}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      <Footer />
    </>
  );
}
